import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { CalendarRepository } from '../domain/calendar.repository';
import {
  CalendarActivitySuggestion,
  CalendarAxis,
  CalendarAxisInput,
  CalendarEntry,
  CalendarEntryInput,
  CalendarEntryUpdate,
  CalendarHighlight,
  CalendarRangeData
} from '../models/calendar.model';
import {
  CalendarWeekTemplate,
  CalendarWeekTemplateItem,
  CalendarWeekTemplateItemInput
} from '../models/calendar-template.model';

interface AxisRow {
  id: number;
  name: string;
  kind: 'TIME_SLOT' | 'AXIS';
  system_key: string | null;
  position: number;
  is_visible: boolean;
  is_archived: boolean;
  color: string | null;
  icon: string | null;
}

interface EntryRow {
  id: number;
  calendar_date: string;
  axis_id: number;
  position: number;
  activity_id: number | null;
  title: string | null;
  note: string | null;
  color: string | null;
  status: 'PLANNED' | 'COMPLETED' | 'CANCELLED' | null;
  completed_at: string | null;
  cancelled_at: string | null;
  sport_plan_kind: 'WORKOUT' | 'ACTIVITY' | null;
  workout_template_id: number | null;
  planned_sport_id: number | null;
}

interface ActivitySuggestionRow {
  id: number;
  name: string;
  archived_at: string | null;
}

interface HighlightRow {
  id: number;
  highlight_date: string;
  entry_id: number | null;
  title: string | null;
}

interface WeekTemplateRow {
  id: number;
  name: string;
  created_from_week_start: string | null;
}

interface WeekTemplateItemRow {
  id: number;
  template_id: number;
  day_offset: number;
  axis_id: number;
  position: number;
  activity_id: number;
  title: string;
  note: string | null;
  color: string | null;
}

@Injectable()
export class SupabaseCalendarRepository extends CalendarRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  loadRange(startDate: string, endDate: string): Promise<CalendarRangeData> {
    return this.loadRangeData(startDate, endDate, true);
  }

  loadPlanningRange(startDate: string, endDate: string): Promise<CalendarRangeData> {
    return this.loadRangeData(startDate, endDate, false);
  }

  private async loadRangeData(
    startDate: string,
    endDate: string,
    includeActivitySuggestions: boolean
  ): Promise<CalendarRangeData> {
    const { error: defaultsError } = await this.client.rpc('ensure_default_calendar_axes');
    if (defaultsError) throw defaultsError;

    const [axesResult, entriesResult, highlightsResult, activitiesResult] = await Promise.all([
      this.client
        .from('calendar_axes')
        .select('id,name,kind,system_key,position,is_visible,is_archived,color,icon')
        .order('position')
        .order('id'),
      this.client
        .from('calendar_entries')
        .select('id,calendar_date,axis_id,position,activity_id,title,note,color,status,completed_at,cancelled_at,sport_plan_kind,workout_template_id,planned_sport_id')
        .eq('entry_kind', 'FREE')
        .gte('calendar_date', startDate)
        .lte('calendar_date', endDate)
        .is('deleted_at', null)
        .order('calendar_date')
        .order('axis_id')
        .order('position')
        .order('id'),
      this.client
        .from('calendar_highlights')
        .select('id,highlight_date,entry_id,title')
        .gte('highlight_date', startDate)
        .lte('highlight_date', endDate)
        .order('highlight_date'),
      this.client
        .from('time_activities')
        .select('id,name,archived_at')
        .order('name')
        .order('id')
    ]);

    if (axesResult.error) throw axesResult.error;
    if (entriesResult.error) throw entriesResult.error;
    if (highlightsResult.error) throw highlightsResult.error;
    if (activitiesResult.error) throw activitiesResult.error;

    const activities = activitiesResult.data as ActivitySuggestionRow[] | null ?? [];
    const activityNames = new Map(activities.map(activity => [activity.id, activity.name]));

    return {
      axes: (axesResult.data as AxisRow[] | null ?? []).map(row => this.mapAxis(row)),
      entries: (entriesResult.data as EntryRow[] | null ?? [])
        .filter((row): row is EntryRow & {
          activity_id: number;
          status: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
        } => row.activity_id !== null && row.status !== null && activityNames.has(row.activity_id))
        .map(row => this.mapEntry(row, activityNames.get(row.activity_id) ?? 'Activity')),
      highlights: (highlightsResult.data as HighlightRow[] | null ?? []).map(row => this.mapHighlight(row)),
      activitySuggestions: includeActivitySuggestions
        ? this.mapActivitySuggestions(activities.filter(activity => activity.archived_at === null))
        : []
    };
  }

  async createAxis(input: CalendarAxisInput): Promise<CalendarAxis> {
    const { data, error } = await this.client
      .from('calendar_axes')
      .insert({
        name: input.name,
        kind: input.kind,
        position: input.position,
        is_visible: input.isVisible ?? true,
        color: input.color ?? null,
        icon: input.icon ?? null
      })
      .select('id,name,kind,system_key,position,is_visible,is_archived,color,icon')
      .single();

    if (error) throw error;
    return this.mapAxis(data as AxisRow);
  }

  async updateAxis(
    axisId: number,
    changes: Partial<CalendarAxisInput> & { isArchived?: boolean; archivedAt?: string | null }
  ): Promise<CalendarAxis> {
    const payload: Record<string, unknown> = {};
    if (changes.name !== undefined) payload['name'] = changes.name;
    if (changes.kind !== undefined) payload['kind'] = changes.kind;
    if (changes.position !== undefined) payload['position'] = changes.position;
    if (changes.isVisible !== undefined) payload['is_visible'] = changes.isVisible;
    if (changes.color !== undefined) payload['color'] = changes.color;
    if (changes.icon !== undefined) payload['icon'] = changes.icon;
    if (changes.isArchived !== undefined) payload['is_archived'] = changes.isArchived;
    if (changes.archivedAt !== undefined) payload['archived_at'] = changes.archivedAt;

    const { data, error } = await this.client
      .from('calendar_axes')
      .update(payload)
      .eq('id', axisId)
      .select('id,name,kind,system_key,position,is_visible,is_archived,color,icon')
      .single();

    if (error) throw error;
    return this.mapAxis(data as AxisRow);
  }

  async createEntry(input: CalendarEntryInput): Promise<CalendarEntry> {
    const { data, error } = await this.client.rpc('save_calendar_activity_entry', {
      p_entry_id: null,
      p_activity_id: input.activityId,
      p_activity_name: input.title,
      p_calendar_date: input.calendarDate,
      p_axis_id: input.axisId,
      p_position: input.position,
      p_note: input.note,
      p_color: input.color ?? null
    });
    if (error) throw error;
    return this.loadEntry(Number(data));
  }

  async updateEntry(entryId: number, input: CalendarEntryUpdate): Promise<CalendarEntry> {
    const { data, error } = await this.client.rpc('save_calendar_activity_entry', {
      p_entry_id: entryId,
      p_activity_id: input.activityId,
      p_activity_name: input.title,
      p_calendar_date: input.calendarDate,
      p_axis_id: input.axisId,
      p_position: input.position,
      p_note: input.note,
      p_color: input.color ?? null
    });
    if (error) throw error;
    return this.loadEntry(Number(data));
  }

  async setEntryPosition(entryId: number, position: number): Promise<void> {
    const { error } = await this.client
      .from('calendar_entries')
      .update({ position })
      .eq('id', entryId)
      .eq('entry_kind', 'FREE')
      .is('deleted_at', null);
    if (error) throw error;
  }

  async completeEntry(entryId: number): Promise<void> {
    const { error } = await this.client
      .from('calendar_entries')
      .update({
        status: 'COMPLETED',
        completed_at: new Date().toISOString(),
        cancelled_at: null
      })
      .eq('id', entryId)
      .eq('entry_kind', 'FREE')
      .is('deleted_at', null);
    if (error) throw error;
  }

  async reopenEntry(entryId: number): Promise<void> {
    const { error } = await this.client
      .from('calendar_entries')
      .update({ status: 'PLANNED', completed_at: null, cancelled_at: null })
      .eq('id', entryId)
      .eq('entry_kind', 'FREE')
      .is('deleted_at', null);
    if (error) throw error;
  }

  async cancelEntry(entryId: number): Promise<void> {
    const { error } = await this.client
      .from('calendar_entries')
      .update({
        status: 'CANCELLED',
        completed_at: null,
        cancelled_at: new Date().toISOString()
      })
      .eq('id', entryId)
      .eq('entry_kind', 'FREE')
      .is('deleted_at', null);
    if (error) throw error;
  }

  async deleteEntry(entryId: number): Promise<void> {
    const { error } = await this.client
      .from('calendar_entries')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', entryId)
      .eq('entry_kind', 'FREE')
      .is('deleted_at', null);
    if (error) throw error;
  }

  setHighlightTitle(date: string, title: string): Promise<CalendarHighlight> {
    return this.saveHighlight(date, { title, entry_id: null });
  }

  setHighlightEntry(date: string, entryId: number): Promise<CalendarHighlight> {
    return this.saveHighlight(date, { title: null, entry_id: entryId });
  }

  async clearHighlight(date: string): Promise<void> {
    const { error } = await this.client
      .from('calendar_highlights')
      .delete()
      .eq('highlight_date', date);
    if (error) throw error;
  }

  async loadWeekTemplates(): Promise<CalendarWeekTemplate[]> {
    const { data: templates, error: templatesError } = await this.client
      .from('calendar_week_templates')
      .select('id,name,created_from_week_start')
      .order('name')
      .order('id');
    if (templatesError) throw templatesError;

    const rows = templates as WeekTemplateRow[] | null ?? [];
    if (!rows.length) return [];

    const { data: items, error: itemsError } = await this.client
      .from('calendar_week_template_items')
      .select('id,template_id,day_offset,axis_id,position,activity_id,title,note,color')
      .in('template_id', rows.map(template => template.id))
      .order('day_offset')
      .order('axis_id')
      .order('position')
      .order('id');
    if (itemsError) throw itemsError;

    const itemRows = items as WeekTemplateItemRow[] | null ?? [];
    return rows.map(template => this.mapWeekTemplate(
      template,
      itemRows.filter(item => item.template_id === template.id)
    ));
  }

  async createWeekTemplate(name: string): Promise<CalendarWeekTemplate> {
    const { data, error } = await this.client
      .from('calendar_week_templates')
      .insert({ name: name.trim() })
      .select('id,name,created_from_week_start')
      .single();
    if (error) throw error;
    return this.mapWeekTemplate(data as WeekTemplateRow, []);
  }

  async createWeekTemplateFromWeek(name: string, weekStart: string): Promise<CalendarWeekTemplate> {
    const { data, error } = await this.client.rpc('create_calendar_week_template_from_week', {
      p_name: name.trim(),
      p_week_start: weekStart
    });
    if (error) throw error;
    return this.loadWeekTemplate(Number(data));
  }

  async createWeekTemplateItem(
    templateId: number,
    input: CalendarWeekTemplateItemInput
  ): Promise<CalendarWeekTemplateItem> {
    const { data, error } = await this.client.rpc('save_calendar_week_template_activity_item', {
      p_template_id: templateId,
      p_day_offset: input.dayOffset,
      p_axis_id: input.axisId,
      p_position: input.position,
      p_activity_id: input.activityId ?? null,
      p_activity_name: input.title,
      p_note: input.note,
      p_color: input.color ?? null
    });
    if (error) throw error;
    return this.loadWeekTemplateItem(Number(data));
  }

  async deleteWeekTemplateItem(itemId: number): Promise<void> {
    const { error } = await this.client
      .from('calendar_week_template_items')
      .delete()
      .eq('id', itemId);
    if (error) throw error;
  }

  async deleteWeekTemplate(templateId: number): Promise<void> {
    const { error } = await this.client
      .from('calendar_week_templates')
      .delete()
      .eq('id', templateId);
    if (error) throw error;
  }

  async applyWeekTemplate(templateId: number, weekStart: string): Promise<number> {
    const { data, error } = await this.client.rpc('apply_calendar_week_template', {
      p_template_id: templateId,
      p_week_start: weekStart
    });
    if (error) throw error;
    return Number(data ?? 0);
  }

  private async saveHighlight(
    date: string,
    source: { title: string | null; entry_id: number | null }
  ): Promise<CalendarHighlight> {
    const { data: existing, error: lookupError } = await this.client
      .from('calendar_highlights')
      .select('id')
      .eq('highlight_date', date)
      .maybeSingle();
    if (lookupError) throw lookupError;

    const query = existing
      ? this.client
          .from('calendar_highlights')
          .update(source)
          .eq('id', existing.id)
      : this.client
          .from('calendar_highlights')
          .insert({ highlight_date: date, ...source });

    const { data, error } = await query
      .select('id,highlight_date,entry_id,title')
      .single();
    if (error) throw error;
    return this.mapHighlight(data as HighlightRow);
  }

  private async loadEntry(entryId: number): Promise<CalendarEntry> {
    const { data: row, error } = await this.client
      .from('calendar_entries')
      .select('id,calendar_date,axis_id,position,activity_id,title,note,color,status,completed_at,cancelled_at,sport_plan_kind,workout_template_id,planned_sport_id')
      .eq('id', entryId)
      .eq('entry_kind', 'FREE')
      .is('deleted_at', null)
      .single();
    if (error) throw error;

    const entry = row as EntryRow;
    if (entry.activity_id === null || entry.status === null) {
      throw new Error('Invalid Calendar Activity entry.');
    }

    const { data: activity, error: activityError } = await this.client
      .from('time_activities')
      .select('name')
      .eq('id', entry.activity_id)
      .single();
    if (activityError) throw activityError;

    return this.mapEntry(
      entry as EntryRow & {
        activity_id: number;
        status: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
      },
      activity.name
    );
  }

  private async loadWeekTemplateItem(itemId: number): Promise<CalendarWeekTemplateItem> {
    const { data, error } = await this.client
      .from('calendar_week_template_items')
      .select('id,template_id,day_offset,axis_id,position,activity_id,title,note,color')
      .eq('id', itemId)
      .single();
    if (error) throw error;
    return this.mapWeekTemplateItem(data as WeekTemplateItemRow);
  }

  private async loadWeekTemplate(templateId: number): Promise<CalendarWeekTemplate> {
    const [{ data: template, error: templateError }, { data: items, error: itemsError }] = await Promise.all([
      this.client
        .from('calendar_week_templates')
        .select('id,name,created_from_week_start')
        .eq('id', templateId)
        .single(),
      this.client
        .from('calendar_week_template_items')
        .select('id,template_id,day_offset,axis_id,position,activity_id,title,note,color')
        .eq('template_id', templateId)
        .order('day_offset')
        .order('axis_id')
        .order('position')
        .order('id')
    ]);
    if (templateError) throw templateError;
    if (itemsError) throw itemsError;
    return this.mapWeekTemplate(
      template as WeekTemplateRow,
      items as WeekTemplateItemRow[] | null ?? []
    );
  }

  private mapActivitySuggestions(rows: ActivitySuggestionRow[]): CalendarActivitySuggestion[] {
    return rows.slice(0, 50).map(row => ({
      id: row.id,
      title: row.name
    }));
  }

  private mapAxis(row: AxisRow): CalendarAxis {
    return {
      id: row.id,
      name: row.name,
      kind: row.kind,
      systemKey: row.system_key,
      position: row.position,
      isVisible: row.is_visible,
      isArchived: row.is_archived,
      color: row.color,
      icon: row.icon
    };
  }

  private mapEntry(
    row: EntryRow & {
      activity_id: number;
      status: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
    },
    activityName: string
  ): CalendarEntry {
    return {
      id: row.id,
      calendarDate: row.calendar_date,
      axisId: row.axis_id,
      position: row.position,
      activityId: row.activity_id,
      title: activityName,
      note: row.note,
      color: row.color,
      status: row.status,
      completedAt: row.completed_at,
      cancelledAt: row.cancelled_at,
      sportPlanKind: row.sport_plan_kind,
      workoutTemplateId: row.workout_template_id,
      plannedSportId: row.planned_sport_id
    };
  }

  private mapHighlight(row: HighlightRow): CalendarHighlight {
    return {
      id: row.id,
      highlightDate: row.highlight_date,
      entryId: row.entry_id,
      title: row.title
    };
  }

  private mapWeekTemplate(
    row: WeekTemplateRow,
    items: WeekTemplateItemRow[]
  ): CalendarWeekTemplate {
    return {
      id: row.id,
      name: row.name,
      createdFromWeekStart: row.created_from_week_start,
      items: items.map(item => this.mapWeekTemplateItem(item))
    };
  }

  private mapWeekTemplateItem(row: WeekTemplateItemRow): CalendarWeekTemplateItem {
    return {
      id: row.id,
      templateId: row.template_id,
      dayOffset: row.day_offset,
      axisId: row.axis_id,
      position: row.position,
      activityId: row.activity_id,
      title: row.title,
      note: row.note,
      color: row.color
    };
  }
}
